<template>
  <modal name="rating-modal" width="95%" height="auto" :scrollable="true">
    <div class="modern-rating-modal">
      <!-- Close Button -->
      <button @click="hide('rating-modal')" class="close-button">
        <span class="mdi mdi-close"></span>
      </button>

      <!-- Loader -->
      <div v-if="loader" class="loader-overlay">
        <div class="spinner"></div>
      </div>

      <!-- Modal Content -->
      <div class="modal-content-wrapper">
        <div class="modal-header-section">
          <h3 class="modal-title">Rate Your Experience</h3>
          <p class="modal-subtitle">How would you rate this?</p>
        </div>

        <!-- Star Rating -->
        <div class="rating-section">
          <p v-if="selectedRating > 0" class="rating-description">
            {{ rateDescriptions[selectedRating - 1] }}
          </p>
          <div class="stars-container">
            <button v-for="star in 5" :key="star" @click="selectRating(star)" @mouseenter="hoveredRating = star"
              @mouseleave="hoveredRating = 0" class="star-button"
              :class="{ active: star <= (hoveredRating || selectedRating) }">
              <span class="mdi"
                :class="star <= (hoveredRating || selectedRating) ? 'mdi-star' : 'mdi-star-outline'"></span>
            </button>
          </div>
        </div>
        <!-- Message Box -->
        <div class="message-section">
          <textarea v-model="message" class="message-textarea" placeholder="Write a comment about your experience..."
            rows="3"></textarea>
        </div>

        <!-- Action Buttons -->
        <div class="action-buttons">
          <button @click="handleConfirm" class="btn-confirm" :disabled="loader || selectedRating === 0">
            <span v-if="!loader">{{ $t("action.confirm") }}</span>
            <span v-else>Submitting...</span>
          </button>
          <button @click="hide('rating-modal')" class="btn-cancel" :disabled="loader">
            {{ $t("action.cancel") }}
          </button>
        </div>
      </div>
    </div>
  </modal>
</template>

<script>
export default {
  props: {
    ratingModel: {
      type: String,
      required: true,
    },
    modelToRates: {
      type: String,
      required: true,
    },
    ratingModelId: {
      type: Number,
      required: true,
    },
    modelToRatesId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      loader: false,
      selectedRating: 0,
      hoveredRating: 0,
      message: "",
      rateDescriptions: ['Very bad', 'Bad', 'Normal', 'Good', 'Very good'],
    };
  },
  methods: {
    hide(modalName) {
      this.selectedRating=0;
      this.hoveredRating=0;
      this.message="";
      this.$modal.hide(modalName);
    },
    selectRating(rating) {
      this.selectedRating=rating;
    },
    handleConfirm() {
      if (this.selectedRating===0) {
        alert('Please select a rating before confirming');
        return;
      }

      const data={
        ratingModel: this.ratingModel,
        ratingModelId: this.ratingModelId,
        modelToRates: this.modelToRates,
        modelToRatesId: this.modelToRatesId,
        rates: this.selectedRating,
        message: this.message,
      };

      this.loader=true;

      this.$axios({
        url: 'ratings/create',
        method: 'POST',
        data: data,
      })
        .then(() => {
          this.loader=false;
          this.hide('rating-modal');
          // Emit event to parent component
          this.$emit('rating-submitted');
        })
        .catch(() => {
          this.loader=false;
          alert('Failed to submit rating. Please try again.');
        });
    },
  },
  computed: {
    tokenExpiryDate: {
      get() {
        return this.$store.state.tokenExpiryDate;
      },
      set(expiryDate) {
        this.$store.commit('set_token_expiry_date', { expiryDate: expiryDate });
      },
    },
    user: {
      get() {
        return this.$store.state.user;
      },
      set(user) {
        this.$store.commit('setUser', { user: user });
      },
    },
  },
};
</script>
<style scoped>
.modern-rating-modal {
  position: relative;
  background: white;
  border-radius: var(--border-radius);
  padding: 0;
  min-height: 300px;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  background: transparent;
  border: none;
  font-size: 24px;
  color: var(--gray-500);
  cursor: pointer;
  padding: 8px;
  border-radius: var(--border-radius-sm);
  transition: all 0.2s ease;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-button:hover {
  background: var(--gray-100);
  color: var(--gray-700);
}

.loader-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--border-radius);
  z-index: 20;
}

.spinner {
  width: 48px;
  height: 48px;
  border: 4px solid var(--gray-200);
  border-top-color: var(--color-nz);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.modal-content-wrapper {
  padding: 40px 32px 32px;
}

.modal-header-section {
  text-align: center;
  margin-bottom: 32px;
}

.modal-title {
  font-size: 24px;
  font-weight: 600;
  color: var(--gray-900);
  margin: 0 0 8px 0;
}

.modal-subtitle {
  font-size: 14px;
  color: var(--gray-600);
  margin: 0;
}

.rating-section {
  text-align: center;
  margin-bottom: 32px;
}

.stars-container {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-bottom: 16px;
}

.star-button {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  transition: transform 0.2s ease;
  font-size: 40px;
  color: var(--gray-300);
}

.star-button:hover {
  transform: scale(1.15);
}

.star-button.active {
  color: var(--color-id-200);
}

.star-button .mdi {
  display: block;
}

.rating-description {
  font-size: 16px;
  font-weight: 500;
  color: var(--color-nz-200);
  margin: 0;
  min-height: 24px;
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 576px) {
  .modal-content-wrapper {
    padding: 32px 20px 24px;
  }

  .modal-title {
    font-size: 20px;
  }

  .stars-container {
    gap: 8px;
  }

  .star-button {
    font-size: 32px;
    padding: 4px;
  }

  .action-buttons {
    flex-direction: column;
  }


}
</style>